/**
 *  This component is the entry point for the tenantSwithcer
 *  Reducers are invoke to fetch the data globally and passed to child components.
 **/

import { useState, useCallback, useEffect, useMemo, cloneElement } from 'react'
import { Button, Body, TruncateText } from '@panwds/react-ui'
import { ProgressCircleIcon } from '@panwds/icons'
import { dispatch as globalDispatch, actions, getFrameworkVar } from '@sparky/framework'
import { blueSteelScale } from '@panwds/tailwindcss'

// Contexts
import { useCommonDrawerState } from '../common/context/CommonDrawerContexts'
import CommonServiceContext from '../common/context/CommonServiceContext'

// Components
import CommonDrawer from '../common/components/drawer/CommonDrawer'
import TsgNavIcon from './TsgNavIcon'
import GlobalModule from '../GlobalModule'
import TenantSwitcherPage from './components/tenantSwitcherPage/TenantSwitcherPage'

// Utils
import { getVarValue } from '../common/utils/Sparky'

// Constants
import { VIEWING } from './constants/LabelConstants'
import { TSG_NONE, VIEW_TENANT_SWITCHER } from '../common/constants/GlobalConstants'
import { FF_USE_NEW_LEFT_NAV } from '../common/utils/FeatureFlags'

const hideGlobalModuleDrawer = getFrameworkVar(FF_USE_NEW_LEFT_NAV)

const TenantSwitcher = (props) => {
  /**
   * =========== Global Reducer ==============
   */

  /**
   * Merge all reducer into single reducer
   */
  const {
    commonModule,
    auth,
    children,
    mapActions,
    isSideNavCollapsed,
    isTenantSwitcherLoading,
    fallback,
  } = props
  const contextTsgID = auth?.tsg_id || ''
  const commonDrawerState = useCommonDrawerState(commonModule)

  const {
    treeReducer,
    viewReducer,
    instanceReducer,
    appReducer,
    licenseReducer,
    iamReducer,
    deviceReducer,
    subscriptionReducer,
    dispatch,
  } = commonDrawerState

  /**
   * =========== Local State =================
   */
  const isOpen = commonModule?.path === VIEW_TENANT_SWITCHER

  const [height, setHeight] = useState()

  const showSubscriptions = getVarValue('show_subscriptions', false)

  const isDrawerOrSwitcherOpen = Boolean(isOpen || commonModule?.visible)
  useEffect(() => { // APPORTAL-6197 ensure pai tenant dropdown not on top of common modules
    const paiFloatDropdown = isDrawerOrSwitcherOpen && document.querySelector('#actionBar_tenantSelector')
    if (paiFloatDropdown && !paiFloatDropdown?.style?.zIndex) {
      paiFloatDropdown.style.zIndex = '0'
    }
    return () => {
      if (paiFloatDropdown?.style?.zIndex === '0') {
        paiFloatDropdown.style.zIndex = ''
      }
    }
  }, [isDrawerOrSwitcherOpen])

  // useEffect(() => {
  //   globalDispatch.setAuthValues({ tsgs: treeReducer.sgTree })
  // }, [treeReducer.sgTree])
  /***
   * ========== Callback =================
   */

  /**
   *
   * @type {(function(): void)|*}
   */
  const handleToggle = useCallback(() => {
    if (contextTsgID === TSG_NONE.id || !Boolean(contextTsgID)) {
      return
    }
    const path = commonModule?.path === VIEW_TENANT_SWITCHER ? '' : VIEW_TENANT_SWITCHER
    actions.setValues({
      commonModule: {
        visible: false,
        path
      }
    })
  }, [commonModule?.path, contextTsgID])

  const tenantSwitcher = useMemo(() => {
    if (!isOpen) {
      return null
    }
    const handleHeightChange = (value) => {
      setHeight(value)
    }
    return (
      <TenantSwitcherPage
        globalContextTsgId={contextTsgID}
        globalDispatch={globalDispatch}
        handleHeightonToggle={handleHeightChange}
        showSubscriptions={showSubscriptions}
        {...subscriptionReducer}
        {...treeReducer}
        {...viewReducer}
        {...instanceReducer}
        {...licenseReducer}
        mapActions={mapActions}
        dispatch={dispatch}
        pageHeaderProps={{
          label: VIEWING,
          onClose: handleToggle,
          showCloseButton: true,
          subTitle: VIEWING,
        }}
      >
        {children}
      </TenantSwitcherPage>
    )
  }, [isOpen, contextTsgID, showSubscriptions, subscriptionReducer, treeReducer, viewReducer, instanceReducer, licenseReducer, mapActions, dispatch, handleToggle, children])

  const switcherButton = useMemo(() => {
    if (fallback) {
      return cloneElement(fallback, {
        onClick: handleToggle
      })
    }

    if (isTenantSwitcherLoading) {
      return <Button
        size='lg'
        disabled={contextTsgID === TSG_NONE.id || !Boolean(contextTsgID)}
        style={{ color: blueSteelScale[300] }}
        addClassName={'tw-mb-2 tw-flex tw-items-baseline cm-pointer-events-none focus:tw-outline-none focus:tw-ring-offset-0 outline:none tw-min-w-full tw-text-base'}
        appearance='tertiary-clear'
        icon={<ProgressCircleIcon size='md' className={'tw-animate-spin'} />}>
        <div className={'tw-animate-pulse tw-flex tw-space-x-4 w-100'}>
          <div className={'tw-h-4 tw-ml-1 tw-flex-1 tw-bg-blue-steel-300  tw-rounded-md'} />
        </div>
      </Button>
    }

    if (isSideNavCollapsed) {
      return <Button
        appearance='tertiary-clear'
        disabled={contextTsgID === TSG_NONE.id || !Boolean(contextTsgID)}
        addClassName={'switcher-button'}
        style={{ padding: '10px 0 20px 10px' }}
        onClick={handleToggle}
        aria-label={'icononly'}
        icon={<TsgNavIcon />}
      />
    }

    return <Button
      disabled={contextTsgID === TSG_NONE.id || !Boolean(contextTsgID)}
      size='lg'
      style={{ color: blueSteelScale[300] }}
      addClassName='switcher-button tw-mb-2 tw-bg-blue-steel-900 dark:cm-bg-black focus:tw-outline-none hover:tw-bg-blue-steel-800 focus:tw-bg-blue-steel-900'
      appearance='tertiary-clear'
      icon={<TsgNavIcon />}
      onClick={handleToggle}
    >
      <div className={'cm-ml-[5px] tw-flex tw-flex-col tw-items-baseline'}>
        <div style={{ width: 180 }} className={'tw-text-blue-steel-300 tw-flex cm-text-[12px]'}><TruncateText>{treeReducer.globalTsgName}</TruncateText></div>
        <Body appearance={'secondary'} addClassName={'tw-text-blue-steel-300 cm-text-[12px]'}>Tenants and Services</Body>
      </div>
    </Button>
  }, [contextTsgID, fallback, handleToggle, isSideNavCollapsed, isTenantSwitcherLoading, treeReducer.globalTsgName])

  return (
    <CommonServiceContext
      auth={auth}
      commonModule={commonModule}
      commonDrawerState={commonDrawerState}
    >
      {switcherButton}
      <CommonDrawer
        isOpen={isOpen}
        width='85vw'
        height={height}
        color='rgb(25, 34, 46);'
        showCloseButton={true}
        onClose={handleToggle}
        isSideNavCollapsed={isSideNavCollapsed}
      >
        {tenantSwitcher}
      </CommonDrawer>
      {!hideGlobalModuleDrawer && <GlobalModule
        {...appReducer}
        {...treeReducer}
        {...viewReducer}
        {...instanceReducer}
        {...{
          showSubscriptions,
          dispatch,
          iamReducer,
          licenseReducer,
          deviceReducer,
          globalDispatch,
          commonModule,
          subscriptionReducer,
          auth
        }}
      // selectedTreeNode={commonModule?.tsg}
      />}
    </CommonServiceContext>
  )
}
export default TenantSwitcher
